import type { JSX } from 'react';
import type { PageTemplate } from '@packages/recommendations/src/types';
import type { ParagraphType } from '../../../interfaces/components';

/**
 * Component function type - accepts data and optional props passed by ComponentRecognizer
 */
export type ComponentFunction<K extends ParagraphType['type']> = ({
  data,
  className,
  pageTemplate,
  recoIndex,
  token,
  style,
}: {
  data: Extract<ParagraphType, { type: K }>;
  className?: string;
  pageTemplate?: PageTemplate;
  recoIndex?: number;
  token?: string;
  style?: React.CSSProperties;
}) => JSX.Element | null;

/**
 * Component map
 */
type ComponentMap = {
  [K in ParagraphType['type']]?: ComponentFunction<K>;
};

/**
 * Internal registry storage
 */
const componentRegistry: ComponentMap = {};

/**
 * Register components with metadata.
 * @param map - Registration objects with { component, isClient } structure
 */
export const registerComponents = (map: ComponentMap): void => {
  Object.entries(map).forEach(([type, registration]) => {
    if (registration) {
      componentRegistry[type as ParagraphType['type']] = registration as any;
    }
  });
};

/**
 * Gets a registered component function
 */
export const getComponent = <T extends ParagraphType['type']>(
  type: T,
): ComponentFunction<T> | undefined => componentRegistry[type];

/**
 * Resets the component registry (for testing purposes only)
 * @internal
 */
export const resetComponentRegistry = (): void => {
  Object.keys(componentRegistry).forEach((key) => {
    delete componentRegistry[key as ParagraphType['type']];
  });
};
